import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { MediaUpload } from '@/components/common/MediaUpload';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import { useUpdateOffer } from '../../hooks/useOfferManagement';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import { updateOfferSchema, type UpdateOfferInput } from '../../schemas/offerSchemas';
import type { Offer } from '../../types';

interface EditOfferFormProps {
  offer: Offer;
  onClose: () => void;
  formId?: string;
}

export function EditOfferForm({
  offer,
  onClose,
  formId = 'edit-offer-form',
}: EditOfferFormProps) {
  const [isUploading, setIsUploading] = useState(false);
  const updateOffer = useUpdateOffer();
  const uploadMutation = useMediaUpload({ folder: 'offers' });

  const form = useForm<UpdateOfferInput>({
    resolver: zodResolver(updateOfferSchema),
    defaultValues: {
      short_description: offer.short_description,
      detailed_description: offer.detailed_description,
      media_url: offer.media_url,
      expires_at: new Date(offer.expires_at),
      claim_frequency: offer.claim_frequency,
      farming_time_in_hours: offer.farming_time_in_hours,
      is_active: offer.is_active,
    },
  });

  // Update form when offer changes (e.g., if drawer reopens with different offer)
  useEffect(() => {
    form.reset({
      short_description: offer.short_description,
      detailed_description: offer.detailed_description,
      media_url: offer.media_url,
      expires_at: new Date(offer.expires_at),
      claim_frequency: offer.claim_frequency,
      farming_time_in_hours: offer.farming_time_in_hours,
      is_active: offer.is_active,
    });
  }, [offer, form]);

  const handleMediaUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const response = await uploadMutation.mutateAsync(file);
      setIsUploading(false);
      return response;
    } catch (error) {
      setIsUploading(false);
      throw error;
    }
  };

  const onSubmit = async (data: UpdateOfferInput) => {
    try {
      // Only send changed fields
      const changedFields: Partial<UpdateOfferInput> = {};
      let hasChanges = false;

      Object.keys(data).forEach((key) => {
        const fieldKey = key as keyof UpdateOfferInput;
        const newValue = data[fieldKey];
        const oldValue = offer[fieldKey as keyof Offer];

        // Special handling for dates
        if (fieldKey === 'expires_at' && newValue && oldValue) {
          const newDate = new Date(newValue as any).getTime();
          const oldDate = new Date(oldValue as any).getTime();
          if (newDate !== oldDate) {
            changedFields[fieldKey] = newValue as any;
            hasChanges = true;
          }
        } else if (newValue !== oldValue) {
          changedFields[fieldKey] = newValue as any;
          hasChanges = true;
        }
      });

      if (hasChanges) {
        await updateOffer.mutateAsync({
          id: offer.id,
          data: changedFields as UpdateOfferInput,
        });
      }
      onClose();
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6"
        data-testid="edit-offer-form"
      >
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Venue</h4>
          <p className="text-sm text-muted-foreground">
            {offer.venue_name} (ID: {offer.venue_id})
          </p>
        </div>

        <FormField
          control={form.control}
          name="short_description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Short Description</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., 20% off all coffee drinks"
                  data-testid="edit-offer-form-short-description"
                />
              </FormControl>
              <FormDescription>
                Brief description shown in offer lists (max 255 characters)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="detailed_description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Detailed Description</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Enter full offer details, terms and conditions..."
                  rows={4}
                  data-testid="edit-offer-form-detailed-description"
                />
              </FormControl>
              <FormDescription>
                Complete offer description with all details (max 1000 characters)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="media_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Offer Image</FormLabel>
              <FormControl>
                <MediaUpload
                  value={field.value}
                  onChange={field.onChange}
                  onUpload={handleMediaUpload}
                  isUploading={isUploading}
                  onError={(error) => form.setError('media_url', { message: error })}
                />
              </FormControl>
              <FormDescription>
                Current image shown above. Upload a new image to replace it (JPEG or PNG, max 5MB)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="expires_at"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Expiry Date & Time</FormLabel>
              <FormControl>
                <DateTimePicker
                  value={field.value}
                  onChange={field.onChange}
                  minDate={new Date()}
                  error={!!form.formState.errors.expires_at}
                />
              </FormControl>
              <FormDescription>
                When this offer expires (must be in the future)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="claim_frequency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Claim Frequency</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={1}
                    max={100}
                    data-testid="edit-offer-form-claim-frequency"
                    onChange={e => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormDescription>
                  Max claims per user (1-100)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="farming_time_in_hours"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Farming Time (hours)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={168}
                    data-testid="edit-offer-form-farming-time"
                    onChange={e => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormDescription>
                  Hours until redeemable (0-168)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Active Status</FormLabel>
                <FormDescription>
                  Enable or disable this offer
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid="edit-offer-form-is-active"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="space-y-2 text-sm text-muted-foreground">
          <p>Created: {new Date(offer.created_at).toLocaleString()}</p>
          <p>Last updated: {new Date(offer.updated_at).toLocaleString()}</p>
          <p>Claims: {offer.total_claims} total, {offer.active_claims} active, {offer.redeemed_claims} redeemed</p>
        </div>
      </form>
    </Form>
  );
}